import React, { useCallback, useLayoutEffect, useMemo } from 'react'
import LoginUI from './Login.UI'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import useYupInputSchemaBuilder from '../../modules/formManagement/hooks/useYupInputSchemaBuilder'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import storeHooks from '../../redux/hooks'
import { useDispatch } from 'react-redux'
import AuthExperienceActions from '../../redux/actions/experiences/Auth'
import { Navigate, useLocation } from 'react-router-dom'
import { ROUTES } from '../../modules/routing/config/constants'
import { isTwoFactorAuthDisabled } from '../../config/runtimeEnv'
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import CopyRight from '../../components/layout/CopyRight'

const LOGIN_BACKGROUND_AVIF = '/images/login-background.avif'
const LOGIN_BACKGROUND_WEBP = '/images/login-background.webp'
const LOGIN_BACKGROUND_JPEG = '/images/login-background.jpg'

const loginFooterCopyRightSx = {
  color: 'rgba(255, 255, 255, 0.72)',
  py: 1
}

const AuthPageContainer = styled(Box)(
  ({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
    height: '100vh',
    overflow: 'hidden',
    // Brand navy while the image loads / if it fails
    backgroundColor: theme.palette.customColors.praetorianMain
  })
)

const AuthPageBackground = styled(Box)({
  position: 'absolute',
  inset: 0,
  zIndex: 0,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    display: 'block'
  }
})

const AuthPageScrim = styled(Box)({
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  pointerEvents: 'none',
  // Soft dark overlay so the login card stays readable on the map art
  backgroundColor: 'rgba(0, 0, 0, 0.35)'
})

const AuthPageContent = styled(Box)({
  position: 'relative',
  zIndex: 2,
  flex: '1 1 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 0,
  width: '100%'
})

const AuthPageFooter = styled(Box)({
  position: 'relative',
  zIndex: 2,
  flexShrink: 0,
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
})

const Login = () => {
  const { t } = useTranslation()
  const inputSchemaBuilder = useYupInputSchemaBuilder()
  const isLoading = storeHooks.useLoading()
  const dispatch = useDispatch()
  const location = useLocation()
  const { isAuthenticated, authCodePrefix, authCodePrefixFetched, loginError } = storeHooks.useAuthState()

  const onLoginRedirectPath = location.state?.from || { pathname: ROUTES.ADMIN.ABSOLUTE_PATH }

  useLayoutEffect(() => {
    // fetch the auth code prefix the first time the login screen is displayed
    dispatch(
      AuthExperienceActions.fetchAuthCodePrefix()
    )
  }, [dispatch])

  const loginFormLabels = useMemo(
    () => ({
      title: t('Login.Title', 'Sign in'),
      emailInput: t('Login.EmailInput', 'Email Address'),
      passwordInput: t('Login.PasswordInput', 'Password'),
      rememberMe: t('Login.RememberMe', 'Remember me'),
      authCodeInput: t('Login.AuthCodeInput', 'Auth code'),
      loginButton: t('Login.LoginButton', 'Sign In'),
      loginWithWSSButton: t('Login.LoginWithWSSButton', 'Sign in with WSS'),
      forgotPasswordLinkLabel: t('Login.ForgotPasswordLinkLabel', 'Forgot password?')
    }),
    [t]
  )

  const twoFactorDisabled = isTwoFactorAuthDisabled()
  const twoFASecondStep = !twoFactorDisabled && !!authCodePrefix

  const formSchema = useMemo(
    () => yup.object({
      email: inputSchemaBuilder.Email(!twoFASecondStep),
      password: inputSchemaBuilder.Text(!twoFASecondStep),
      rememberMe: inputSchemaBuilder.Boolean(false),
      authCode: inputSchemaBuilder.Text(twoFASecondStep)
    }),
    [inputSchemaBuilder, twoFASecondStep]
  )

  const form = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
      authCode: ''
    }
  })

  const { handleSubmit: formHandleSubmit } = form

  const handleDismissLoginError = useCallback(
    () => {
      dispatch(AuthExperienceActions.clearLoginError())
    },
    [dispatch]
  )

  const handleSubmit = useCallback(
    (...args) => formHandleSubmit(
      data => !twoFASecondStep
        ? dispatch(
          AuthExperienceActions.logIn(
            data.email,
            data.password
          )
        )
        : dispatch(
          AuthExperienceActions.validateAuthCode(
            data.authCode,
            data.rememberMe
          )
        )
    )(...args),
    [formHandleSubmit, twoFASecondStep, dispatch]
  )

  if (isAuthenticated) {
    return (
      <Navigate
        to={onLoginRedirectPath}
        replace
      />
    )
  }

  return (
    <FormProvider {...form}>
      <AuthPageContainer>
        <AuthPageBackground
          component='picture'
          aria-hidden
        >
          <source
            srcSet={LOGIN_BACKGROUND_AVIF}
            type='image/avif'
          />
          <source
            srcSet={LOGIN_BACKGROUND_WEBP}
            type='image/webp'
          />
          <img
            src={LOGIN_BACKGROUND_JPEG}
            alt=''
            width={1024}
            height={512}
            decoding='sync'
            fetchPriority='high'
          />
        </AuthPageBackground>
        <AuthPageScrim aria-hidden />
        <AuthPageContent>
          <LoginUI
            labels={loginFormLabels}
            onSubmit={handleSubmit}
            forgotPasswordPageLink='#'
            twoFAAuthCodePrefix={authCodePrefix}
            logoURI='/android-chrome-192x192.png'
            loginError={loginError}
            onDismissLoginError={handleDismissLoginError}
            displayMode={(
              twoFASecondStep
                ? LoginUI.DISPLAY_MODES.TWO_FA_SECOND_STEP
                : LoginUI.DISPLAY_MODES.TWO_FA_FIRST_STEP
            )}
            disabled={isLoading || (!twoFactorDisabled && !authCodePrefixFetched)}
          />
        </AuthPageContent>
        <AuthPageFooter component='footer'>
          <CopyRight sx={loginFooterCopyRightSx} />
        </AuthPageFooter>
      </AuthPageContainer>
    </FormProvider>
  )
}

export default Login
